import create from 'zustand';
import * as R from 'ramda';

export const createUseExpectedPathChartStore = (
  initialState = {
    chartData: {},
    goalsData: []
  }
) =>
  create(set => ({
    ...initialState,
    reset: () => {
      set(initialState);
    },
    initialize(initialData) {
      set(
        state => ({
          ...state,
          ...initialData
        }),
        true
      );
    },
    setChartData(selectedGoalsIds) {
      set(state => {
        const currentDate = new Date();

        const selectedGoalsData = selectedGoalsIds
          ? state.goalsData.filter(({ goalId }) =>
              selectedGoalsIds.includes(goalId)
            )
          : state.goalsData;

        const timeHorizon = Math.max(
          ...selectedGoalsData.map(({ timeHorizon }) => timeHorizon)
        );

        const forecast = selectedGoalsData
          .map(({ forecast }) => forecast)
          .flat()
          .reduce((acc, curr) => {
            const index = acc.findIndex(({ date }) => date === curr.date);
            if (index !== -1) {
              return acc.map((item, i) =>
                i === index
                  ? R.mergeWithKey(
                      (k, l, r) => (k === 'date' ? r : R.add(l, r)),
                      acc[index],
                      curr
                    )
                  : item
              );
            }
            return [...acc, curr];
          }, []);

        const chartData = {
          expectedPathTable: mapServerDataToExpectedPathTable(
            timeHorizon,
            currentDate,
            forecast
          ),
          expectedReturn: mapServerDataToExpectedReturnChart(
            timeHorizon,
            currentDate,
            forecast
          ),
          bounds: mapServerDataToBoundsChart(timeHorizon, currentDate, forecast)
        };
        return { chartData };
      });
    },
    addGoalData: goalData => {
      set(state => ({ ...state, goalsData: [...state.goalsData, goalData] }));
    },
    editGoalData: goalData => {
      set(state => ({
        ...state,
        goalsData: state.goalsData.map(data => {
          if (goalData.goalId === data.goalId) {
            return goalData;
          }
          return data;
        })
      }));
    },
    resetChartData: () => {
      set({
        chartData: R.propOr({}, 'chartData', initialState),
        goalsData: R.propOr([], 'goalsData', initialState)
      });
    }
  }));

const filterExpectedPathServerData = (yearOffset, currentDate, forecast) => {
  const { currentMonth, currentYear } = currentDate;
  const finalYear = currentYear + yearOffset;

  return R.pipe(
    R.map(i => {
      const { date: dateStr, ...restProps } = i;
      const date = new Date(dateStr);

      return {
        ...restProps,
        year: date.getFullYear(),
        month: date.getMonth()
      };
    }),
    R.groupBy(R.prop('year')),
    R.toPairs,
    R.filter(g => g[0] <= finalYear),
    R.sort(R.ascend(R.prop(0))),
    R.map(g => {
      const items = g[1];
      const currentMonthItem = R.find(i => i.month === currentMonth, items);

      return currentMonthItem;
    }),
    R.reject(R.isNil)
  )(forecast);
};

const mapServerDataToExpectedPathTable = (
  timeHorizon,
  currentDate,
  forecast
) => {
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const filteredData = R.filter(
    i => i.year > currentYear,
    filterExpectedPathServerData(
      timeHorizon,
      {
        currentYear,
        currentMonth
      },
      forecast
    )
  );

  return R.map(
    i => ({
      year: i.year,
      expectedReturn: i.value,
      lowerBound: i.lower_bound,
      upperBound: i.upper_bound
    }),
    filteredData
  );
};

const mapServerDataToExpectedReturnChart = (
  timeHorizon,
  currentDate,
  forecast
) => {
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const filteredData = filterExpectedPathServerData(
    timeHorizon,
    {
      currentYear,
      currentMonth
    },
    forecast
  );

  return R.map(i => [Date.UTC(i.year, i.month), i.value], filteredData);
};

const mapServerDataToBoundsChart = (timeHorizon, currentDate, forecast) => {
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const filteredData = filterExpectedPathServerData(
    timeHorizon,
    { currentYear, currentMonth },
    forecast
  );

  return R.map(
    i => [Date.UTC(i.year, i.month), i.lower_bound, i.upper_bound],
    filteredData
  );
};
